import React, { MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react'
import { AvatarProps, AvatarWithoutDot } from '@components/Avatar/Avatar'
import { ZChannelMessageRedactedEvent } from '@components/MessageTimeline/util/getEventsByDate'
import { ProfileHoverCard } from '@components/ProfileHoverCard/ProfileHoverCard'
import { Reactions } from '@components/Reactions/Reactions'
import { RepliesButton } from '@components/Replies/MessageReplies'
import { TipCounter } from '@components/TipCounter/TipCounter'
import { MessageReactions, MessageTips, Pin, ThreadStatsData } from '@towns-protocol/sdk'
import { Box, BoxProps, ButtonText, Icon, Paragraph, Stack, Text } from '@ui'
import { differenceInDays, format, getYear, isToday, isYesterday } from 'date-fns'
import debug from 'debug'
import { useDevice } from 'hooks/useDevice'
import { useFocused } from 'hooks/useFocused'
import { useHover } from 'hooks/useHover'
import { useMobile } from 'hooks/useMobile'
import { useHandleReaction } from 'hooks/useReactions'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { TextProps } from 'ui/components/Text/Text'
import {
    LookupUser,
    Permission,
    useConnectivity,
    useHasPermission,
    useMyUserId,
    useUserLookup,
} from 'use-towns-client'
import { env } from 'utils'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'

import { AvatarAtoms } from 'components/Avatar/Avatar.css'

import { DATA_ATTR_INTERACTION_REQUEST } from '../MessageTimeIineItem/items/constants'

import { TipReaction } from './tips/TipReaction'
import { DecryptionDebugger } from './DecryptionDebugger'
import { MessageContextMenu } from './MessageContextMenu'
import { MessageModalSheet } from './MessageModalSheet'
import { SendStatus, SendStatusIndicator } from './SendStatusIndicator'

type Props = {
    userId?: string | null
    senderId?: string
    avatarSize?: AvatarAtoms['size']
    user?: LookupUser
    displayContext?: 'single' | 'head' | 'body' | 'tail'
    messageSourceAnnotation?: string
    messageBody?: string
    reactions?: MessageReactions
    replies?: ThreadStatsData
    canReply?: boolean
    timestamp?: number
    editing?: boolean
    editable?: boolean
    eventId?: string
    latestEventId?: string
    threadParentId?: string
    highlight?: boolean
    selectable?: boolean
    isEncryptedMessage?: boolean
    listView?: boolean
    channelId?: string
    spaceId?: string
    children?: React.ReactNode
    onReaction?: ReturnType<typeof useHandleReaction>
    relativeDate?: boolean
    rounded?: BoxProps['rounded']
    padding?: BoxProps['padding']
    background?: BoxProps['background']
    channelLabel?: string
    isChannelWritable?: boolean
    isChannelReactable?: boolean
    sendStatus?: SendStatus
    sessionId?: string
    pin?: Pin
    canPin?: boolean
    tips?: MessageTips
} & BoxProps

export type MessageLayoutProps = Props

function relativeDate(date: number, baseDate: number, shorten?: boolean) {
    if (isToday(date)) {
        return format(date, 'h:mm a')
    }

    if (isYesterday(date)) {
        return format(date, shorten ? "'Yesterday' h:mm a" : "'Yesterday at' h:mm a")
    }

    const daysDiff = differenceInDays(date, baseDate)

    if (Math.abs(daysDiff) < 6) {
        return format(date, shorten ? 'EEE h:mm a' : "EEEE 'at' h:mm a")
    }

    const currentYear = getYear(baseDate)
    const dateYear = getYear(date)

    return dateYear === currentYear
        ? shorten
            ? format(date, 'MMM do')
            : format(date, "MMMM do 'at' h:mm a")
        : shorten
          ? format(date, 'MMM do, yyyy')
          : format(date, "MMMM do, yyyy 'at' h:mm a")
}

export const MessageLayout = React.memo((props: Props) => {
    const {
        userId,
        senderId,
        threadParentId,
        eventId,
        latestEventId,
        avatarSize = 'avatar_md',
        user,
        messageBody,
        messageSourceAnnotation,
        channelId,
        spaceId,
        editable: isEditable,
        editing: isEditing,
        highlight: isHighlight,
        isChannelWritable = true,
        isChannelReactable,
        selectable: isSelectable,
        isEncryptedMessage,
        listView: isListView,
        displayContext = 'single',
        onReaction,
        reactions,
        relativeDate: isRelativeDate,
        replies,
        canPin,
        canReply,
        sendStatus,
        timestamp,
        children,
        sessionId,
        pin,
        tips,
        ...boxProps
    } = props

    const { loggedInWalletAddress } = useConnectivity()

    const { hasPermission: canRedact } = useHasPermission({
        spaceId: spaceId ?? '',
        walletAddress: loggedInWalletAddress ?? '',
        permission: Permission.Redact,
        channelId,
    })

    const ref = useRef<HTMLDivElement>(null)
    const { isTouch } = useDevice()
    const isMobile = useMobile()

    const { isHover, onMouseEnter } = useHover(ref)
    const { isFocused } = useFocused(ref)

    const [isModalSheetVisible, setIsModalSheetVisible] = useState(false)

    const isActive = isTouch ? isFocused : isHover

    const useShortDate = isMobile
    const date = timestamp
        ? isRelativeDate
            ? relativeDate(timestamp, Date.now(), useShortDate)
            : format(timestamp, 'h:mm a')
        : undefined

    const getFullDateTooltip = (timestamp: number) => {
        return format(timestamp, "MMM do 'at' h:mm:ss a")
    }

    const backgroundProps = useHighlightBackground(isHighlight, !!pin, props.background)

    const onClick: MouseEventHandler = useCallback(
        (e) => {
            // skip context menu when clicking on links, video elements, or interaction requests (bot forms)
            if (
                e.target instanceof Element &&
                (e.target.closest('a') ||
                    e.target.closest('video') ||
                    e.target.closest(`[${DATA_ATTR_INTERACTION_REQUEST}]`))
            ) {
                return
            }
            setIsModalSheetVisible(true)
        },
        [setIsModalSheetVisible],
    )

    const { openPanel } = usePanelActions()
    const messageOwner = useUserLookup(senderId ?? '')

    const handleUsernameClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            if (senderId) {
                openPanel('profile', { profileId: senderId })
            }
        },
        [openPanel, senderId],
    )

    const hasReplies = replies && replies.replyEventIds.size > 0 && eventId
    const numReactions = reactions ? Object.values(reactions).length : 0
    const hasReactions = reactions && numReactions
    const displayButtonsInRow = numReactions < 3 && isTouch

    const debugHash = debug.enabled('app:vlist') ? ` [${eventId?.substring(0, 4)}]` : ''
    const showDecryptionDebugger = debug.enabled('app:decryption')

    const onMobileTap = isTouch && isSelectable ? onClick : undefined
    const streamId = channelId ?? spaceId

    return (
        <Stack
            horizontal
            ref={ref}
            background="level1"
            onMouseEnter={onMouseEnter}
            {...boxProps}
            hoverable={isSelectable}
            elevate={isEditing && !pin}
            hoverActive={isEditing || isHighlight}
            {...backgroundProps}
            tabIndex={props.tabIndex ?? 0}
        >
            {/* left / avatar gutter */}
            {/* snippet: center avatar with name row by keeping the size of the containers equal  */}
            <Box minWidth={isTouch ? 'x5' : 'x6'} onClick={onMobileTap}>
                {displayContext === 'single' || displayContext === 'head' ? (
                    senderId ? (
                        <AvatarComponent
                            size={avatarSize}
                            insetTop="2"
                            insetBottom="lg"
                            userId={senderId}
                            tooltip={isTouch ? undefined : <ProfileHoverCard userId={senderId} />}
                            onClick={senderId ? handleUsernameClick : undefined}
                        />
                    ) : (
                        <></>
                    )
                ) : (
                    <>
                        {!isRelativeDate && isActive && (
                            <Box
                                paddingTop="xxs"
                                insetBottom="xxs"
                                display={{ default: 'flex', touch: 'none' }}
                                tooltip={timestamp ? getFullDateTooltip(timestamp) : undefined}
                            >
                                <Paragraph truncate size="xs" color="gray2">
                                    {date}
                                    {debugHash}
                                </Paragraph>
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* right / main content */}
            <Stack grow gap="paragraph" position="relative" minWidth="none">
                {pin ? <PinnedContent pin={pin} /> : <></>}
                {/* name & date top row */}
                {(displayContext === 'head' || displayContext === 'single') && (
                    <Stack
                        horizontal
                        grow
                        height="x1.5"
                        userSelect="none"
                        gap="x0.75"
                        onClick={onMobileTap}
                    >
                        {/* display name with tooltip */}
                        {senderId && (
                            <>
                                <Box
                                    tooltip={
                                        isTouch ? undefined : <ProfileHoverCard userId={senderId} />
                                    }
                                >
                                    <UserName user={user} onClick={handleUsernameClick} />
                                </Box>
                                {user?.isBot && <BotBadge />}
                                <TipCounter userId={senderId} />
                            </>
                        )}
                        {props.channelLabel ? (
                            <Text color="gray2">{`#${props.channelLabel}`}</Text>
                        ) : (
                            ``
                        )}

                        {/* date, alignment tbc depending on context */}
                        {date && (
                            <Box
                                justifyContent="center"
                                tooltip={timestamp ? getFullDateTooltip(timestamp) : undefined}
                            >
                                <Text
                                    shrink={false}
                                    fontSize="sm"
                                    color="gray2"
                                    as="span"
                                    textAlign={isListView ? 'right' : 'left'}
                                >
                                    {date}
                                    {debugHash}
                                </Text>
                            </Box>
                        )}
                    </Stack>
                )}
                <Stack gap={{ default: 'paragraph', mobile: 'paragraph' }}>
                    <Stack
                        grow
                        fontSize="md"
                        color="gray1"
                        gap="sm"
                        onClick={isTouch && isSelectable ? onClick : undefined}
                    >
                        {children}
                    </Stack>
                    {/* channel */}
                    {messageSourceAnnotation && (
                        <ButtonText color="gray2" as="span">
                            {messageSourceAnnotation}
                        </ButtonText>
                    )}
                    {hasReactions || hasReplies || tips ? (
                        <Stack
                            alignItems={displayButtonsInRow ? 'center' : undefined}
                            flexDirection={displayButtonsInRow ? 'row' : 'columnReverse'}
                            gap={
                                displayButtonsInRow
                                    ? { default: 'md', mobile: 'sm' }
                                    : { default: 'sm', mobile: 'paragraph' }
                            }
                        >
                            {hasReplies && (
                                <RepliesButton eventId={eventId} threadStats={replies} />
                            )}
                            {hasReactions ? (
                                <Reactions
                                    userId={userId}
                                    parentId={eventId}
                                    reactions={reactions}
                                    tipReaction={
                                        env.VITE_TIPS_ENABLED ? (
                                            <TipReaction
                                                isTippable={!isEditable}
                                                tips={tips}
                                                eventId={eventId}
                                                messageOwner={messageOwner}
                                                key={`tip-${eventId}`}
                                                streamId={streamId}
                                                reactions={reactions}
                                            />
                                        ) : (
                                            <></>
                                        )
                                    }
                                    onReaction={onReaction}
                                />
                            ) : tips && env.VITE_TIPS_ENABLED ? (
                                <Box alignSelf="start">
                                    <TipReaction
                                        isTippable={!isEditable}
                                        tips={tips}
                                        eventId={eventId}
                                        messageOwner={messageOwner}
                                        streamId={streamId}
                                        reactions={reactions}
                                    />
                                </Box>
                            ) : null}
                        </Stack>
                    ) : (
                        <></>
                    )}
                    {sendStatus && <SendStatusIndicator status={sendStatus} />}
                </Stack>

                {!isTouch &&
                    channelId &&
                    eventId &&
                    isActive &&
                    !isEditing &&
                    (isSelectable || isEncryptedMessage) && (
                        <MessageContextMenu
                            canEdit={isEditable}
                            canReact={isChannelReactable}
                            canPin={canPin}
                            canReply={canReply && isChannelWritable}
                            canRedact={canRedact}
                            channelId={channelId}
                            eventId={eventId}
                            latestEventId={latestEventId}
                            isFocused={isFocused}
                            isPinned={!!pin}
                            isEncryptedMessage={isEncryptedMessage}
                            spaceId={spaceId}
                            threadParentId={threadParentId}
                            messageOwner={messageOwner}
                        />
                    )}
            </Stack>
            {isModalSheetVisible && isTouch && channelId && eventId && isSelectable && (
                <MessageModalSheet
                    canPin={canPin}
                    canReply={canReply && isChannelWritable}
                    canReact={isChannelReactable}
                    canRedact={canRedact}
                    channelId={channelId}
                    spaceId={spaceId}
                    eventId={eventId}
                    canEdit={isEditable && isChannelWritable}
                    messageBody={messageBody}
                    threadParentId={threadParentId}
                    isPinned={!!pin}
                    messageOwner={messageOwner}
                    onClose={() => setIsModalSheetVisible(false)}
                />
            )}
            {showDecryptionDebugger && sessionId && eventId && (
                <DecryptionDebugger sessionId={sessionId} eventId={eventId} timestamp={timestamp} />
            )}
        </Stack>
    )
})

const UserName = ({
    user,
    onClick,
    color = 'default',
}: {
    user?: LookupUser
    onClick: (e: React.MouseEvent) => void
    color?: TextProps['color']
}) => {
    const name = user ? getPrettyDisplayName(user) : ''
    const ensName = user?.ensName

    return (
        <Box cursor="pointer" data-testid="username-text" color={color} onClick={onClick}>
            <Text truncate fontWeight="strong" as="span">
                {ensName || name}
            </Text>
        </Box>
    )
}

export const RedactedMessageLayout = (props: {
    event: ZChannelMessageRedactedEvent
    replies?: ThreadStatsData
}) => {
    const { event, replies } = props
    const { isTouch } = useDevice()

    return (
        <Stack horizontal hoverable gap="md" paddingLeft="lg" paddingY="md" background="level1">
            <Box
                centerContent
                rounded="full"
                height={isTouch ? 'x4' : 'x6'}
                width={isTouch ? 'x4' : 'x6'}
                background="level2"
            >
                <Icon type="delete" color="gray2" padding="xs" />
            </Box>
            <Box>
                <Stack gap>
                    <Box
                        padding
                        horizontal
                        border
                        gap="sm"
                        alignItems="center"
                        color="gray2"
                        background="level1"
                        height="x6"
                        rounded="sm"
                    >
                        <Paragraph>This message was deleted</Paragraph>
                    </Box>
                    {replies && replies.replyEventIds?.size > 0 && event.eventId && (
                        <Box justifySelf="start">
                            <RepliesButton eventId={event.eventId} threadStats={replies} />
                        </Box>
                    )}
                </Stack>
            </Box>
        </Stack>
    )
}

const useHighlightBackground = (
    isHighlight?: boolean,
    pinned?: boolean,
    defaultBackground?: BoxProps['background'],
) => {
    const [isHighlightActive, setHighlightActive] = useState(isHighlight)

    const background = isHighlightActive
        ? ('level2' as const)
        : (defaultBackground ?? (pinned ? ('positiveSubtle' as const) : undefined))

    useEffect(() => {
        if (isHighlightActive) {
            const timeout = setTimeout(() => {
                setHighlightActive(false)
            }, 1000)
            return () => {
                clearTimeout(timeout)
            }
        }
    })

    const [backgroundTransitionEnabled, setBackgroundTransitionEnabled] = useState(isHighlight)

    const onTransitionEnd = isHighlight
        ? () => {
              setBackgroundTransitionEnabled(false)
          }
        : undefined

    const style = backgroundTransitionEnabled ? { transition: `background 1s ease` } : undefined

    return background ? { onTransitionEnd, style, background } : { onTransitionEnd, style }
}

const AvatarComponent = (
    props: AvatarProps & { userId?: string; onClick?: (e: React.MouseEvent) => void },
) => {
    const { userId, onClick, ...avatarProps } = props
    if (userId && onClick) {
        return <ClickableAvatar {...avatarProps} userId={userId} onClick={onClick} />
    }
    return <AvatarWithoutDot {...avatarProps} userId={userId} />
}

const ClickableAvatar = (
    props: AvatarProps & { userId: string; onClick: (e: React.MouseEvent) => void },
) => {
    const { userId, onClick, ...avatarProps } = props

    return (
        <Box cursor="pointer" data-testid="clickable-avatar" onClick={onClick}>
            <AvatarWithoutDot userId={userId} {...avatarProps} />
        </Box>
    )
}

const PinnedContent = (props: { pin: Pin }) => {
    const myUserId = useMyUserId()
    const { pin } = props
    const user = useUserLookup(pin.creatorUserId)
    return (
        <Stack horizontal grow gap="xs" height="height_sm" alignItems="center" color="cta1">
            <Box>
                <Icon type="pinFill" size="square_xxs" />
            </Box>
            <Box>
                <Paragraph size="sm">
                    Pinned by {myUserId === pin.creatorUserId ? 'you' : getPrettyDisplayName(user)}
                </Paragraph>
            </Box>
        </Stack>
    )
}

const BotBadge = () => {
    return (
        <Box justifyContent="end" height="paragraph">
            <Icon
                type="robot"
                size="square_xs"
                color="gray2"
                // because the SVG is surrounded by blank space
                insetBottom="1"
            />
        </Box>
    )
}
